// Typed Suitability Records API client for the web layer (three-tier client
// model Stage 2, ADR-0015).
//
// Wraps the `suitabilityRecordsContract` endpoints via the shared `api`
// client and exposes react-query hooks. Mutations route their
// `{ status, body }` envelope through `runStaffEndpoint` and invalidate
// `suitabilityRecordsKeys.all` so the directory and the detail view
// re-render together.
//
// The compliance wall (D5) needs NOTHING here: `assessmentContent` rides the
// detail payload and is simply null for desk/ops callers.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, runStaffEndpoint, staffErrorMessage } from '@/lib/api';
import type {
    CreateSuitabilityRecordRequest,
    CreateSuitabilityRecordWithFactFindRequest,
    ListSuitabilityRecordsResponse,
    StaffActionResult,
    SuitabilityRecordApprovalAction,
    SuitabilityRecordClientCandidatesResponse,
    SuitabilityRecordDetail,
    SuitabilityRecordLifecycleAction,
    SuitabilityRecordStatus,
    UpdateSuitabilityRecordRequest,
} from '@septima/contract';

export interface SuitabilityRecordsQuery {
    search?: string;
    status?: SuitabilityRecordStatus;
}

export const suitabilityRecordsKeys = {
    all: ['suitability-records'] as const,
    list: (q?: SuitabilityRecordsQuery) => ['suitability-records', 'list', q ?? {}] as const,
    detail: (recordId: string) => ['suitability-records', 'detail', recordId] as const,
    clientCandidates: (recordId: string) =>
        ['suitability-records', 'client-candidates', recordId] as const,
};

function assertOk(result: StaffActionResult): StaffActionResult {
    if (!result.ok) throw new Error(result.error ?? 'Request failed');
    return result;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useSuitabilityRecords(
    query?: SuitabilityRecordsQuery,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: suitabilityRecordsKeys.list(query),
        enabled: options?.enabled ?? true,
        queryFn: async (): Promise<ListSuitabilityRecordsResponse> => {
            const res = await api.suitabilityRecords.listSuitabilityRecords({
                query: query ?? {},
            });
            if (res.status !== 200) throw new Error(`Suitability records failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

export function useSuitabilityRecordDetail(recordId: string | undefined) {
    return useQuery({
        queryKey: suitabilityRecordsKeys.detail(recordId ?? ''),
        enabled: Boolean(recordId),
        queryFn: async (): Promise<SuitabilityRecordDetail> => {
            const res = await api.suitabilityRecords.getSuitabilityRecord({
                params: { recordId: recordId! },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 5_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations (via runStaffEndpoint)
// ---------------------------------------------------------------------------

export function useCreateSuitabilityRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateSuitabilityRecordRequest): Promise<string> => {
            const result = assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.createSuitabilityRecord({ body: input }),
                ),
            );
            return (result as { recordId?: string }).recordId ?? '';
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

export function useUpdateSuitabilityRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            recordId: string;
            patch: UpdateSuitabilityRecordRequest;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.updateSuitabilityRecord({
                        params: { recordId: input.recordId },
                        body: input.patch,
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

export function useSetSuitabilityRecordStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            recordId: string;
            action: SuitabilityRecordLifecycleAction;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.setSuitabilityRecordStatus({
                        params: { recordId: input.recordId },
                        body: { action: input.action },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

/** Compliance sign-off pair (ADR-0015 D6) — approve / withdraw, comply only. */
export function useSetSuitabilityRecordApproval() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            recordId: string;
            action: SuitabilityRecordApprovalAction;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.setSuitabilityRecordApproval({
                        params: { recordId: input.recordId },
                        body: { action: input.action },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// SEP-178 — create the record's satellite artefacts in place
// ---------------------------------------------------------------------------

/**
 * The clients this record links to — the CLOSED answer set for a
 * SUR-originated create. Desk-only endpoint; `retry: false` so an
 * ops/comply 403 surfaces as "no data" rather than a retry storm (the
 * sections that consume it only render for Desk anyway).
 */
export function useSuitabilityRecordClientCandidates(
    recordId: string | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: suitabilityRecordsKeys.clientCandidates(recordId ?? ''),
        enabled: Boolean(recordId) && (options?.enabled ?? true),
        retry: false,
        queryFn: async (): Promise<SuitabilityRecordClientCandidatesResponse> => {
            const res = await api.suitabilityRecords.listSuitabilityRecordClientCandidates({
                params: { recordId: recordId! },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 10_000,
    });
}

export interface CreatedArtefact {
    /** The client the artefact was keyed to — the deep-link target. */
    clientId: string;
    /** False when an existing artefact was linked rather than created. */
    created: boolean;
}

/**
 * A new Suitability Record over one or more account groups, its contacts and
 * its first fact find (SEP-353). It answers the handle of the fact find.
 */
export function useCreateSuitabilityRecordWithFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateSuitabilityRecordWithFactFindRequest) => {
            const res = await api.suitabilityRecords.createSuitabilityRecordWithFactFind({
                body: input,
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return {
                recordId: res.body.recordId,
                reference: res.body.reference,
                factFindId: res.body.factFindId,
            };
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all });
            void qc.invalidateQueries({ queryKey: ['fact-find'] });
            void qc.invalidateQueries({ queryKey: ['suis'] });
        },
    });
}

/** Open (or link) the Internal Suitability Assessment and write its link. */
export function useCreateSuitabilityRecordInternalReview() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            recordId: string;
            clientId?: string;
        }): Promise<CreatedArtefact> => {
            const result = assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.createSuitabilityRecordInternalReview({
                        params: { recordId: input.recordId },
                        body: { clientId: input.clientId },
                    }),
                ),
            ) as StaffActionResult & { clientId?: string; created?: boolean };
            return { clientId: result.clientId ?? '', created: result.created ?? false };
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

/**
 * Open (or link) the Discretionary Investment Mandate letter and write
 * `mandateLetterId`. "Mandate" here is the MandateLetter — the client-facing
 * letter the IM issues — settled with the business on 2026-07-31; the
 * free-text `mandateRef` on the Record card is a separate field.
 */
export function useCreateSuitabilityRecordMandateLetter() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            recordId: string;
            clientId?: string;
        }): Promise<CreatedArtefact> => {
            const result = assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.createSuitabilityRecordMandateLetter({
                        params: { recordId: input.recordId },
                        body: { clientId: input.clientId },
                    }),
                ),
            ) as StaffActionResult & { clientId?: string; created?: boolean };
            return { clientId: result.clientId ?? '', created: result.created ?? false };
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}

/** Ops-mediated book move (ADR-0015 D0.2). */
export function useReassignSuitabilityRecordOwner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { recordId: string; ownerImUserId: string }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.suitabilityRecords.reassignSuitabilityRecordOwner({
                        params: { recordId: input.recordId },
                        body: { ownerImUserId: input.ownerImUserId },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all }),
    });
}
